import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { EMOJI_NO_COUNTRY_FLAGS } from "../../utils/emojiPicker";
import i18nDe from "@emoji-mart/data/i18n/de.json";
import { DE } from "../../Languages/DE";
import "./chat-pro.css";

// Emoji panel of the chat composer: a popover anchored above the smiley button (right edges
// aligned), not a full-screen modal. It stays open while emoji are inserted, so several can be
// added in a row; outside click, Escape, the smiley again or sending closes it.

const MARGIN = 8;
const GAP = 10;
const MIN_READY_HEIGHT = 120;

const I18N_DE = {
  ...i18nDe,
  search: DE.search || i18nDe.search,
  categories: {
    ...i18nDe.categories,
    frequent: DE.frequent || i18nDe.categories.frequent,
    people: DE.people || i18nDe.categories.people,
  },
};

export default function ComposerEmojiPicker({
  open,
  closing = false,
  anchorRef,
  onSelect,
  onClose,
  isGerm = false,
  internal = false,
}) {
  const popRef = useRef(null);
  const [pos, setPos] = useState(null);

  const place = useCallback(() => {
    const el = popRef.current;
    const anchor = anchorRef && anchorRef.current;
    if (!el || !anchor) return;
    const r = anchor.getBoundingClientRect();
    const w = el.offsetWidth;
    const h = el.offsetHeight;
    if (h < MIN_READY_HEIGHT) return; // picker not drawn yet: stay hidden until it is
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.right + 4 - w;
    let top = r.top - h - GAP;
    if (top < MARGIN) top = Math.max(MARGIN, r.bottom + GAP);
    left = Math.max(MARGIN, Math.min(left, vw - w - MARGIN));
    top = Math.max(MARGIN, Math.min(top, vh - h - MARGIN));
    setPos({ left, top });
  }, [anchorRef]);

  // em-emoji-picker renders its content asynchronously, so the first measure sees an almost
  // empty box: re-place whenever the real size arrives (or changes).
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return undefined;
    }
    place();
    const el = popRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => place());
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, place]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    const onDown = (e) => {
      if (popRef.current && popRef.current.contains(e.target)) return;
      const anchor = anchorRef && anchorRef.current;
      if (anchor && anchor.contains(e.target)) return; // the smiley toggles on its own
      onClose();
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("resize", place);
    document.addEventListener("mousedown", onDown);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", place);
      document.removeEventListener("mousedown", onDown);
    };
  }, [open, onClose, place, anchorRef]);

  if (!open) return null;

  return createPortal(
    <div
      ref={popRef}
      className={`kjc-emoji-pop${internal ? " kjc-internal" : ""}${closing ? " is-closing" : ""}${pos ? "" : " is-measuring"}`}
      style={pos ? { left: pos.left, top: pos.top } : undefined}
      role="dialog"
      aria-label={isGerm ? "Emoji einfügen" : "Insert emoji"}
      onMouseDown={(e) => {
        // Keep the caret in the composer while picking (the search field still takes focus).
        const target = (e.nativeEvent.composedPath && e.nativeEvent.composedPath()[0]) || e.target;
        if (!(target && target.tagName === "INPUT")) e.preventDefault();
      }}
    >
      <Picker
        data={data}
        i18n={isGerm ? I18N_DE : undefined}
        theme="light"
        set="native"
        noCountryFlags={EMOJI_NO_COUNTRY_FLAGS}
        previewPosition="none"
        skinTonePosition="search"
        searchPosition="sticky"
        navPosition="top"
        perLine={9}
        maxFrequentRows={2}
        emojiSize={24}
        emojiButtonSize={38}
        onEmojiSelect={(e) => {
          const native = e && (e.native || e.emoji);
          if (native) onSelect(native);
        }}
      />
    </div>,
    document.body
  );
}
